import { useEffect, useRef } from 'react';

import { HotelSearchQuery } from 'generated/graphql';

import mapIcon from './mapIcon';

const DEFAULT_MAP_POSITION = { lat: 37.0902, lng: -95.7129 };
const MAP_ZOOM_NATIONAL = 4;
const MAP_ZOOM_LOCAL = 17;
const MAP_ICON_COLOR = '#1F2937';
const HIGHLIGHTED_ICON_COLOR = '#90C63F';

const isValidCoordinate = (lat: number, lng: number): boolean => {
  return (
    lat !== 0 &&
    lng !== 0 &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
};

const createMap = (container: HTMLDivElement): google.maps.Map => {
  return new google.maps.Map(container, {
    zoom: MAP_ZOOM_NATIONAL,
    center: DEFAULT_MAP_POSITION,
    maxZoom: MAP_ZOOM_LOCAL,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    clickableIcons: true,
    panControl: true,
  });
};

const addHotelMarker = (
  map: google.maps.Map,
  hotel: HotelSearchQuery['hotelSearch']['results'][0]['hotel'],
  onClick: (
    hotel: HotelSearchQuery['hotelSearch']['results'][0]['hotel'],
    marker: google.maps.Marker,
  ) => void,
) => {
  const marker = new google.maps.Marker({
    clickable: true,
    animation: google.maps.Animation.DROP,
    position: { lat: hotel.latitude, lng: hotel.longitude },
    map,
    title: hotel.name,
    icon: {
      url: mapIcon(MAP_ICON_COLOR),
    },
  });

  marker.addListener('click', () => {
    onClick(hotel, marker);
  });

  return marker;
};

/**
 * Represents a geographic location for search markers on the map
 */
export interface SearchLocation {
  latitude: number;
  longitude: number;
  description?: string;
}

interface HotelSearchMapProps {
  hotels?: Array<HotelSearchQuery['hotelSearch']['results'][0]['hotel']>;
  highlightedHotelId?: string | null;
  highlightHotel?: (hotelId: string | null) => void;
  searchLocation?: SearchLocation;
}

const HotelSearchMap = ({
  hotels = [],
  highlightedHotelId,
  highlightHotel,
  searchLocation,
}: HotelSearchMapProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map>();
  const markersRef = useRef<Record<string, google.maps.Marker>>({});
  const searchMarkerRef = useRef<google.maps.Marker>();
  const searchInfoWindowRef = useRef<google.maps.InfoWindow>();

  // Cleanup map instance if container unmounts
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current = undefined;
      }
    };
  }, []);

  // Create map instance once
  useEffect(() => {
    if (!mapRef.current && mapContainerRef.current) {
      mapRef.current = createMap(mapContainerRef.current);
    }
  }, []);

  // Manage markers (add/remove) when hotels change
  useEffect(() => {
    if (!mapRef.current) return;

    hotels.forEach((hotel) => {
      const markerId = hotel.id || hotel.amadeusHotelId;
      if (
        hotel.latitude &&
        hotel.longitude &&
        isValidCoordinate(hotel.latitude, hotel.longitude) &&
        mapRef.current &&
        !markersRef.current[markerId]
      ) {
        const marker = addHotelMarker(mapRef.current, hotel, (hotel) => {
          // Highlight the hotel when map pin is clicked
          highlightHotel?.(hotel.amadeusHotelId);
        });
        // Removed mouseover listener - highlighting now only happens on click

        markersRef.current[markerId] = marker;
      }
    });

    // Remove markers for hotels no longer present
    Object.keys(markersRef.current).forEach((id) => {
      if (!hotels.some((hotel) => (hotel.id || hotel.amadeusHotelId) === id)) {
        const marker = markersRef.current[id];
        marker.setMap(null);
        // Clean up event listeners to prevent memory leaks
        google.maps.event.clearListeners(marker, 'click');
        delete markersRef.current[id];
      }
    });
  }, [hotels, highlightHotel]);

  // Highlight the marker for the currently highlighted hotel
  useEffect(() => {
    Object.entries(markersRef.current).forEach(([id, marker]) => {
      if (highlightedHotelId && id === highlightedHotelId) {
        marker.setIcon({ url: mapIcon(HIGHLIGHTED_ICON_COLOR) });
        marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);
      } else {
        marker.setIcon({ url: mapIcon(MAP_ICON_COLOR) });
        marker.setZIndex(undefined);
      }
    });
  }, [highlightedHotelId]);

  // Manage search location marker
  useEffect(() => {
    if (!mapRef.current || !searchLocation) return;

    // Remove existing search marker and info window if any
    if (searchMarkerRef.current) {
      google.maps.event.clearListeners(searchMarkerRef.current, 'mouseover');
      google.maps.event.clearListeners(searchMarkerRef.current, 'mouseout');
      searchMarkerRef.current.setMap(null);
      searchMarkerRef.current = undefined;
    }
    if (searchInfoWindowRef.current) {
      searchInfoWindowRef.current.close();
      searchInfoWindowRef.current = undefined;
    }

    // Create new search marker if location is valid
    if (isValidCoordinate(searchLocation.latitude, searchLocation.longitude)) {
      const marker = new google.maps.Marker({
        position: {
          lat: searchLocation.latitude,
          lng: searchLocation.longitude,
        },
        map: mapRef.current,
        title: searchLocation.description || 'Search Location',
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#3B82F6', // Blue color
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 3,
        },
        zIndex: google.maps.Marker.MAX_ZINDEX + 2, // Ensure it's always on top
      });

      // Create info window for tooltip
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="
            padding: 8px 12px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            font-size: 14px;
            font-weight: 500;
            color: #111827;
            white-space: nowrap;
          ">
            ${searchLocation.description || 'Search Location'}
          </div>
        `,
        disableAutoPan: true,
      });

      // Remove the default close button
      infoWindow.addListener('domready', () => {
        const closeButton = document.querySelector('.gm-ui-hover-effect');
        if (closeButton) {
          closeButton.remove();
        }
      });

      // Show tooltip on hover
      marker.addListener('mouseover', () => {
        infoWindow.open(mapRef.current, marker);
      });

      marker.addListener('mouseout', () => {
        infoWindow.close();
      });

      searchMarkerRef.current = marker;
      searchInfoWindowRef.current = infoWindow;
    }
  }, [searchLocation]);

  // Update bounds to include search location
  useEffect(() => {
    if (!mapRef.current) return;

    if (hotels.length > 0 || searchLocation) {
      const bounds = new google.maps.LatLngBounds();

      // Add search location to bounds if available
      if (
        searchLocation &&
        isValidCoordinate(searchLocation.latitude, searchLocation.longitude)
      ) {
        bounds.extend({
          lat: searchLocation.latitude,
          lng: searchLocation.longitude,
        });
      }

      // Add hotel locations
      hotels.forEach((hotel) => {
        if (
          hotel.latitude &&
          hotel.longitude &&
          isValidCoordinate(hotel.latitude, hotel.longitude)
        ) {
          bounds.extend({ lat: hotel.latitude, lng: hotel.longitude });
        }
      });

      mapRef.current.fitBounds(bounds);
    } else {
      mapRef.current.setCenter(DEFAULT_MAP_POSITION);
      mapRef.current.setZoom(MAP_ZOOM_NATIONAL);
    }
  }, [hotels, searchLocation]);

  return (
    <div
      className="size-full"
      ref={mapContainerRef}
      data-testid="map-container"
    />
  );
};

export default HotelSearchMap;
